import { usersNode, userAddressesNode } from '@/api/firebase'
import { firebaseObjectToArray, firebaseObjectWithId } from '@/utils'
import { async, GET_ASYNC, ASYNC_MUTATIONS } from '@/utils/async'
import { USERS } from 'db'

const state = {
    users: false,
    user: false,
    userHistory: {},
    ...async()
}

const getters = {
    GET_USERS: (state) => firebaseObjectToArray(state.users),
    GET_USER: (state) => firebaseObjectWithId(state.user),
    GET_USER_HISTORY: (state) => state.userHistory,
    GET_ASYNC
}

const mutations = {
    SET_USERS(state, payload) {
        state.users = payload
    },
    SET_USER(state, payload) {
        state.user = payload
    },
    CLEAR_USERS(state) {
        state.users = false
    },
    CLEAR_USER(state) {
        state.user = false
    },
    APPEND_USER_HISTORY(state, payload) {
        state.userHistory = Object.assign(
            {},
            { ...state.userHistory },
            {
                [payload.id]: payload
            }
        )
    },
    ...ASYNC_MUTATIONS()
}

const actions = {
    // =================================== C/U/D Operations ===================================

    // =================================== Read Operations ===================================

    /**
     * FETCH_USERS
     *
     *     Retrieve a list of users and set data in vuex state if not already set.
     *     If empty, set null. Binds a firebase connection.
     *
     * @returns {Void}
     */
    FETCH_USERS({ commit }) {
        commit('REQUEST')
        commit('CLEAR_USERS')
        usersNode.on(
            'value',
            function(data) {
                try {
                    commit('SET_USERS', data.val())
                    commit('SUCCESS')
                } catch (err) {
                    commit('ERROR', err.message)
                }
            },
            function(err) {
                commit('ERROR', err.message)
            }
        )
    },

    /**
     * FETCH_USER_BY_ID
     *
     *     Retrieve a user by id. If empty, set false. Binds a firebase connection.
     *
     * @param payload.id {string} - The id of the store user to retrieve.
     * @returns {Void}
     */
    FETCH_USER_BY_ID({ commit }, payload) {
        commit('REQUEST')
        commit('CLEAR_USER')
        usersNode.child(payload.id).on(
            'value',
            async function(data) {
                try {
                    let address = false
                    const user = data.val()
                    if (user[USERS.fields.preferredAddress]) {
                        address = (await userAddressesNode
                            .child(data.key)
                            .child(user[USERS.fields.preferredAddress])
                            .once('value')).val()
                    }
                    commit('SET_USER', data.val() ? { ...user, id: payload.id, address } : false)
                    commit('SUCCESS')
                } catch (err) {
                    commit('ERROR', err.message)
                }
            },
            function(err) {
                commit('ERROR', err.message)
            }
        )
    },

    /**
     * SEARCH_USER_BY_EMAIL
     *
     *     Check if a user exists with the provided email.
     *
     * @param payload.email {string} - The email of the user.
     * @returns {Array || Null}
     */
    async SEARCH_USER_BY_EMAIL(_, payload) {
        return firebaseObjectToArray(
            (await usersNode
                .orderByChild(USERS.fields.email)
                .equalTo(payload.email)
                .once('value')).val()
        )
    },

    /**
     * SEARCH_USER_BY_ID
     *
     *     Check if a user exists with the provided id.
     *
     * @param payload.id {string} - The id of the user.
     * @returns {Array || Null}
     */
    async SEARCH_USER_BY_ID(_, payload) {
        return (await usersNode.child(payload.id).once('value')).val()
    },

    /**
     * FETCH_USER_EMAIL_ONCE
     *
     *     Fetch the email of a user by user id.
     *
     * @param payload.id {string} - The id of the user.
     * @returns {string}
     */
    async FETCH_USER_EMAIL_ONCE(_, payload) {
        return (await usersNode
            .child(payload.id)
            .child(USERS.fields.email)
            .once('value')).val()
    },

    // =================================== Listener Operations ===================================

    /**
     * REMOVE_USERS_REFERENCE
     *
     *     Unbind a firebase connection that was set.
     */
    REMOVE_USERS_REFERENCE({ commit }) {
        commit('CLEAR')
        commit('CLEAR_USERS')
        usersNode.off()
    },

    /**
     * REMOVE_USER_REFERENCE
     *
     *     Unbind a firebase connection on a child that was set.
     *
     * @param payload.id {string} - The id of the user child.
     */
    REMOVE_USER_REFERENCE({ commit }, payload) {
        commit('CLEAR')
        commit('CLEAR_USER')
        usersNode.child(payload.id).off()
    }
}

export { state, getters, mutations, actions }
